import {
  FormikErrorMessage,
  Modal,
  Row,
  Section,
  Button,
  TextInput
} from 'shared'
import { Formik, Form, FormikHelpers, FormikProps } from 'formik'
import { Panel, Col, ButtonToolbar, SelectPicker, Toggle } from 'rsuite'
import { useMemo } from 'react'
import { notifyError, notifySuccess } from 'utils'
import {
  defaultEmailConfigFormValues,
  EmailConfigForm,
  EMAIL_CONFIG_FORM_FIELDS,
  getEmailConfigValidationSchema,
  getInitialEmailConfigFormValueFromResponse
} from '../utils'
import { EmailConfig } from '../types'
import { useGetObjectTypesQuery, useCreateEmailMutation, useUpdateEmailMutation, useGetSystemMappingIdQuery } from '../../clientApiSlice'


interface CreateEmailProps {
  open: boolean;
  onClose: () => void;
  isEditMode: boolean;
  email?: EmailConfig | null;
  clientId:number;
}
const CreateEditEmail: React.FC<CreateEmailProps> = ({
  open,
  onClose,
  isEditMode,
  email,
  clientId
}) => {
  const { data: objectTypes = [] } = useGetObjectTypesQuery({})
   const { data : systemMappingId, isLoading } = useGetSystemMappingIdQuery({
  sourceClientId: clientId
})

  const emailId = email?.id
  const initialValues = useMemo(() => {
    const data = isEditMode && email
      ? getInitialEmailConfigFormValueFromResponse(email)
      : defaultEmailConfigFormValues
    return data
  }, [isEditMode, email])

  const [createEmail] = useCreateEmailMutation()
  const [updateEmail] = useUpdateEmailMutation()

  const objectTypesOption = (objectTypes || []).map((t) => ({
    label: t,
    value: t
  }))
  const systemMappingOptions = (systemMappingId?.content ?? []).map((item: any) => ({
    label: String(item.id),
    value: item.id
  }))
  const renderFormButtons = (formikProps: FormikProps<EmailConfigForm>) => (
    <ButtonToolbar>
      {isEditMode ? (
        <>
          <Button
            className="secondary"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save changes
          </Button>
        </>
      ) : (
        <>
          <Button
            className="secondary"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save
          </Button>
        </>
      )}
    </ButtonToolbar>
  )



  const onSubmit = async (
    formValues: EmailConfigForm,
    { setSubmitting }: FormikHelpers<EmailConfigForm>
  ) => {
    const emailData = {
      emailId: formValues.emailId,
      systemMappingId: formValues.systemMappingId,
      username: formValues.username,
      appPassword: formValues.appPassword,
      smtpServer: formValues.smtpServer,
      port: formValues.port,
      protocol: formValues.protocol,
      objectType: formValues.objectType,
      notificationEmails: formValues.notificationEmails,
      enable: true,
    }
    try {
      if (isEditMode && emailId) {
        await updateEmail({ id: emailId, emailData }).unwrap()
        notifySuccess('Email updated successfully')
      } else {
        await createEmail(emailData).unwrap()
        notifySuccess('Email created successfully')
      }
      onClose()
    } catch (error) {
      notifyError(
        (error as any)?.data?.errorMessage ||
        (isEditMode ? 'Failed to update email' : 'Failed to create email'),
        {
          containerId: 'modalToast'
        }
      )

    }
    setSubmitting(false)
  }

  return (
    <Modal
      secondary
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit Email' : 'Add Email'}
      size="calc(50% - 120px)"
      body={
        <Formik
          initialValues={initialValues}
          validationSchema={getEmailConfigValidationSchema}
          enableReinitialize
          onSubmit={onSubmit}
        >
          {(formikProps: FormikProps<EmailConfigForm>) => (
            <Form className="create-rule">
              <Panel>
                <Section title="Email Details">
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={EMAIL_CONFIG_FORM_FIELDS.EMAIL_ID}
                        placeholder="Enter email"
                        dataType="string"
                      />
                      <FormikErrorMessage name={EMAIL_CONFIG_FORM_FIELDS.EMAIL_ID} />
                    </Col>
                    <Col xs={12}>
                      <SelectPicker
                        data={systemMappingOptions}
                        placeholder="Select System Mapping Id"
                        value={formikProps.values[EMAIL_CONFIG_FORM_FIELDS.SYSTEM_MAPPING_ID]}
                        onChange={(value) =>
                          formikProps.setFieldValue(
                            EMAIL_CONFIG_FORM_FIELDS.SYSTEM_MAPPING_ID,
                            value
                          )
                        }
                        cleanable
                        searchable={false}
                        style={{ width: '100%' }}
                        disabled={isLoading}
                      />
                      <FormikErrorMessage name={EMAIL_CONFIG_FORM_FIELDS.SYSTEM_MAPPING_ID} />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={EMAIL_CONFIG_FORM_FIELDS.USERNAME}
                        placeholder="Enter username"
                        dataType="string"
                      />
                      <FormikErrorMessage name={EMAIL_CONFIG_FORM_FIELDS.USERNAME} />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={EMAIL_CONFIG_FORM_FIELDS.APP_PASSWORD}
                        placeholder="Enter app password"
                        dataType="string"
                      />
                      <FormikErrorMessage
                        name={EMAIL_CONFIG_FORM_FIELDS.APP_PASSWORD}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={EMAIL_CONFIG_FORM_FIELDS.SMTP_SERVER}
                        placeholder="Enter smtp server"
                        dataType="string"
                      />
                      <FormikErrorMessage name={EMAIL_CONFIG_FORM_FIELDS.SMTP_SERVER} />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={EMAIL_CONFIG_FORM_FIELDS.PORT}
                        placeholder="Enter port"
                        dataType="string"
                      />
                      <FormikErrorMessage name={EMAIL_CONFIG_FORM_FIELDS.PORT} />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <SelectPicker
                        data={objectTypesOption}
                        placeholder="Select Object Type"
                        value={formikProps.values[EMAIL_CONFIG_FORM_FIELDS.OBJECT_TYPE]}
                        onChange={(value) =>
                          formikProps.setFieldValue(
                            EMAIL_CONFIG_FORM_FIELDS.OBJECT_TYPE,
                            value
                          )
                        }
                        cleanable
                        searchable={false}
                        style={{ width: '100%' }}
                      />
                      <FormikErrorMessage name={EMAIL_CONFIG_FORM_FIELDS.OBJECT_TYPE} />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={EMAIL_CONFIG_FORM_FIELDS.PROTOCOL}
                        placeholder="Enter protocol"
                        dataType="string"
                      />
                      <FormikErrorMessage name={EMAIL_CONFIG_FORM_FIELDS.PROTOCOL} />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={EMAIL_CONFIG_FORM_FIELDS.NOTIFICATION_EMAILS}
                        placeholder="Enter notification emails (comma separated)"
                        dataType="string"
                      />
                      <FormikErrorMessage
                        name={EMAIL_CONFIG_FORM_FIELDS.NOTIFICATION_EMAILS}
                      />
                    </Col>
                  </Row>
                </Section>
              </Panel>
              <Panel className="panel-buttons">
                {renderFormButtons(formikProps)}
              </Panel>
            </Form>
          )}
        </Formik>
      }
    />
  )
}
export default CreateEditEmail
